import { isEmpty, isNil } from 'lodash';
import { BaseClient, DEFAULT_JSON_API_HEADERS, Headers, QueryParams } from '~domain/api-clients/base-client';
import { RequestErrorHandler } from '~domain/api-clients/request-error-handlers/base-request-error-handler';
import { UserAccount } from '~domain/model/account/user-account';
import { UserAccountInitialValues } from '~domain/model/account/user-account-initial-values';
import { UserAccountSummary } from '~domain/model/account/user-account-summary';
import { UserAccountUpdate, UserAccountUpdateAdvanced } from '~domain/model/account/user-account-update';
import { UserAccountUpdateMany } from '~domain/model/account/user-account-update-many';
import { PaginatedResult } from '~domain/model/core/paginated-result';
import { GetManyUserAccountsOptions } from '../model/account/get-many-user-accounts-options';

export class UserAccountsClient extends BaseClient {
  constructor(baseUrl: string, headers: Headers = {}, requestErrorHandler?: RequestErrorHandler) {
    super(baseUrl, { ...DEFAULT_JSON_API_HEADERS, ...headers }, requestErrorHandler);
  }

  public async create(apiKey: string, initialValues: UserAccountInitialValues): Promise<UserAccount> {
    const headers = { 'x-api-key': apiKey };
    return await this.create$<UserAccount>('', initialValues, {}, headers).toPromise();
  }

  public async get(accessToken: string, userAccountId: string) {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return await this.fetch$<UserAccountSummary>(userAccountId).toPromise();
  }

  public async getFromApi(apiKey: string, userAccountId: string) {
    this.api.setHeader('x-api-key', apiKey);
    return await this.fetch$<UserAccountSummary>(userAccountId).toPromise();
  }

  public async getMany(accessToken: string, options: GetManyUserAccountsOptions, search?: string) {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);

    const queryParams: QueryParams = { ...options };
    if (!isNil(search) && !isEmpty(search)) {
      queryParams.search = search;
    }

    return await this.fetch$<PaginatedResult<UserAccount>>('', queryParams).toPromise();
  }

  public async update(
    accessToken: string,
    userId: string,
    updates: UserAccountUpdate & Partial<UserAccountUpdateAdvanced>,
  ): Promise<UserAccountSummary> {
    this.api.setHeader('Authorization', `Bearer ${accessToken}`);
    return await this.updatePartially$<UserAccountSummary>(`${userId}`, updates).toPromise();
  }

  public async updateAdvanced(
    apiKey: string,
    userId: string,
    updates: UserAccountUpdate & Partial<UserAccountUpdateAdvanced>,
  ): Promise<UserAccount> {
    this.api.setHeader('x-api-key', apiKey);
    return await this.updatePartially$<UserAccount>(`${userId}`, updates).toPromise();
  }

  public async updateMany(apiKey: string, update: UserAccountUpdateMany) {
    this.api.setHeader('x-api-key', apiKey);
    return await this.updatePartially$('', update).toPromise();
  }
}
